/**
 * TypeScript interfaces for Appointment Lookup feature
 */

export enum ShopKind {
  Rental = 0,
  Comissioned = 1,
}

// Base appointment service interface
export interface AppointmentService {
  id: string;
  referenceId: string;
  kind: number;
  name: string;
  desc: string | null;
  duration: number;
  cleanupDuration: number | null;
  cost: number;
  taxAmount: number;
  taxAddToPrice: boolean;
  localizedName: string | null;
  localizedDesc: string | null;
  createdAt: string;
  updatedAt: string;
  appointmentId: string;
  serviceId: string;
  parentId: string | null;
  costWithoutTaxes: number;
  service: {
    id: string;
    name: string;
    cost: number;
    desc: string | null;
    duration: number;
    cleanupDuration: number;
    kind: number;
    order: number;
    enabled: boolean;
    kioskEnabled: boolean;
    requiresPrepaid: boolean;
    isShopService: boolean;
    costWithTaxes: number;
    costWithoutTaxes: number;
    taxAmount: number;
    createdAt: string;
    serviceCategoryId: string | null;
    localizedName: string | null;
    localizedDesc: string | null;
  };
}

// Address interface
export interface Address {
  id: string;
  street: string;
  street2: string;
  neighborhood: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
  createdAt: string;
  updatedAt: string;
}

// Brand settings interface
export interface BrandSettings {
  appointmentFeeSplitPercentage: number;
  id: string;
  ecommerceSyncIntegration: boolean;
  membershipsEnabled: boolean;
  mailchimpIntegrationEnabled: boolean;
  defaultLanguage: string;
  gaMeasurementId: string | null;
  gaMeasurementSecret: string | null;
  supportedLanguages: string[];
  itemizedChargeDisplayEnabled: boolean;
  staffManagementEnabled: boolean;
  primaryAccountOwner: string;
  customKioskLogoEnabled: boolean;
  displayReservationAds: boolean;
  offerInstantPayout: number;
  metaPixelId: string | null;
  appointmentFeeEnabled: boolean;
  appointmentFee: number;
  appointmentFeePaidBy: string;
  appointmentFeeRevSharePayoutMethod: string;
  createdAt: string;
  updatedAt: string;
  brandId: string;
  customKioskLogoId: string | null;
  categoryFirstBookingEnabled?: boolean;
}

// Brand interface
export interface Brand {
  appointmentFeeAllowed: boolean;
  id: string;
  name: string;
  description: string;
  website: string;
  instagram: string;
  instagramUserId: string | null;
  campaignsEnabled: boolean;
  financingOffersDisabled: boolean | null;
  apiReaderEnabled: boolean | null;
  commissionReportingEnabled: boolean;
  productReportingEnabled: boolean;
  chatEnabled: boolean;
  email: string;
  smsOriginator: string | null;
  smsOriginatorVerified: boolean;
  currency: string;
  rewardsEnabled: boolean;
  showBrandCustomersToShops: boolean;
  showBrandCustomersToBarbers: boolean;
  overrideDefaultNotificationsConsent: boolean;
  hideGiftcardCodeFromReceipt: boolean;
  shopCount: number;
  addressId: string | null;
  customReportingEnabled: boolean;
  conversionTracking: unknown | null;
  bankAccountId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  settings: BrandSettings;
}

// Shop photo interface (shopDetailQuery shape)
export interface Photo {
  url: string;
  thumbnail: string | null;
  id: string;
  image: string;
  thumbnailImage: string | null;
  order: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  shop_photos: {
    createdAt: string;
    updatedAt: string;
    photoId: string;
    shopId: string;
  };
}

// Shop interface
export interface Shop {
  id: string;
  name: string;
  kind: ShopKind;
  alias: string | null;
  heading: string | null;
  enabled: boolean;
  location: {
    crs: {
      type: string;
      properties: {
        name: string;
      };
    };
    type: string;
    coordinates: [number, number];
  };
  phone: string;
  email: string;
  route: string;
  oldRoutes: string[];
  timezone: string;
  website: string;
  adultsOnly: boolean;
  address: Address;
  earlyTipping: boolean;
  requiresPrepayment: boolean;
  allowInPersonPayments: boolean;
  requiresPartialDeposits: boolean;
  requestCardForInPersonPayments: boolean;
  afterpayEnabled?: boolean;
  settings?: {
    paymentDepositsEnabled: boolean;
    groupAppointmentsEnabled?: boolean;
  };
  staticMap?: {
    url: string;
    thumbnail: string | null;
    id: string;
    image: string;
    thumbnailImage: string | null;
    order: number;
    enabled: boolean;
    createdAt: string;
    updatedAt: string;
  };
  photos: Photo[];
  logo?: { url: string; thumbnail: string | null } | null;
  brand?: Brand;
}

// Barber photo interface (no shop_photos join, unlike shop Photo)
export interface BarberPhoto {
  url: string;
  thumbnail?: string | null;
  id?: string;
  order: number;
}

// Barber interface
export interface Barber {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  instagram: string | null;
  instagramUserId: string | null;
  shortLink: string;
  enabled: boolean;
  order: number;
  route: string;
  oldRoutes: string[];
  color: string;
  confirmed: boolean;
  createdAt: string;
  updatedAt: string;
  photos?: BarberPhoto[];
}

// Customer interface
export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  timezone: string | null;
  gender: string | null;
  birthDate: string | null;
  tipPercentage: number;
  notes: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  shops: unknown[];
  barbers: unknown[];
  photos: unknown[];
}

export enum AppointmentStatus {
  Confirmed = 0,
  Cancelled = 1,
}

// Appointment details interface
export interface AppointmentDetails {
  id: string;
  dateTime: string;
  endTime: string;
  duration: number;
  customerNotes: string | null;
  barberNotes: string | null;
  shopNotes: string | null;
  platform: string;
  customDuration: boolean;
  paidInShop: boolean;
  noShow: boolean;
  status: AppointmentStatus;
  tipAmount: number;
  canceled: boolean;
  canceledBy: string | null;
  isRecurring: boolean;
  recurringId: string | null;
  recurrUntil: string | null;
  recurrEveryNumDays: string | null;
  autoChargeRecurring: boolean;
  isRecurringException: boolean;
  isNewBarberCustomer: boolean;
  isNewShopCustomer: boolean;
  retainedBarberCustomer: boolean;
  bookedWithAnyBarber: boolean;
  doNotChargeTax: boolean;
  bookedFromWaitingList: boolean;
  bookedWithMembership: boolean;
  isReservation: boolean;
  confirmationCode: string;
  paymentStatus: string | null;
  migratedOn: string | null;
  saleOrderId: string | null;
  imported: boolean;
  barberReady: boolean;
  waiver: string | null;
  waiverViewLink: string | null;
  isUnique: boolean;
  customerIp: string | null;
  groupId: string | null;
  metadata: Record<string, unknown>;
  appointmentRating: string | null;
  appointmentRatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  bookedById: string;
  barberId: string;
  customerId: string;
  shopId: string;
  appointment_service: AppointmentService[];
  shop: Shop;
  barber: Barber;
  customer: Customer;
  totalCost: number;
  costWithTaxes: number;
  costWithoutTaxes: number;
  totalWithTip: number;
  totalCostNoTaxesAndDiscount: number;
  totalCostNoDiscount: number;
  totalCostWithoutTaxes: number;
  taxAmount: number;
  refundedAmount: number;
  maxRefundAmount: number;
  tipFee: number;
  payment: unknown;
  allowAppointmentRating: boolean;
  costs: {
    tips: {
      availableTips: Array<{
        percentage: number;
        amount: number;
        flatAmount: number;
      }>;
      amount: number;
      useFlatTips: boolean;
    };
    services: {
      breakdown: Array<{
        id: string;
        cost: number;
        taxAmount: number;
        costWithoutTax: number;
      }>;
      totalWithTaxes: number;
      totalWithoutTaxes: number;
      totalTaxes: number;
    };
    taxes: {
      breakdown: unknown[];
      total: number;
    };
    fees: {
      breakdown: {
        customerBookingFee: number;
        waitingListFee: number;
      };
      total: number;
    };
    promos: {
      breakdown: unknown[];
      total: number;
    };
    discounts: {
      breakdown: unknown[];
      total: number;
    };
    refundedAmount: number;
    depositAmount: number;
    total: number;
  };
}

// Customer profile state interface
export interface CustomerProfileState {
  code: 'sufficient' | 'insufficient' | 'incomplete';
  missingFields: string[];
  customer: Customer;
}

// Main appointment lookup response interface
// Backend reservation states (ios-flagship Reservation.State + lookup values).
export type ReservationState =
  | 'confirm'
  | 'remind'
  | 'between'
  | 'rebook'
  | 'show'
  | 'default'
  | 'complete'
  | 'pending';

export interface AppointmentLookupResponse {
  state: ReservationState;
  profileState: CustomerProfileState;
  message: string;
  appointment: AppointmentDetails;
  upcomingAppointments: AppointmentDetails[];
  pastAppointments: AppointmentDetails[];
}

// API Error interface
export interface AppointmentLookupError {
  code: string;
  message: string;
}

// Screen props interfaces
export interface LookUpScreenProps {
  // Props will be added as needed during implementation
}

export interface AppointmentHomeScreenProps {
  appointment?: AppointmentDetails;
  customer?: Customer;
  message?: string;
}

export interface AppointmentSignInScreenProps {
  customer?: Customer;
  onClose?: () => void;
}

// Form validation interfaces
export interface LookUpFormData {
  confirmationCode: string[];
}

// Navigation params
export interface AppointmentNavigationParams {
  appointmentData?: AppointmentLookupResponse;
  customerId?: string;
  email?: string;
  phone?: string;
}
